import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { useAppSelector } from 'store/hook';
import useAppTheme from 'hooks/useAppTheme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import PushNotificationPreferenceHandler from 'handlers/pushNotificationPreference.handler';
import Permission from 'components/Permission/Permission';
import { NoAccess } from 'components/Modals/NoAccessModal/NoAccessModal';
import { useRoleBasedAccess } from 'hooks/useRoleBasedAccess';
import { type Role } from 'components/Permission/permission.types';

// Only these roles may even see TPMS notification preferences.
const VIEW_ROLES: Role[] = [
  'director',
  'internal-user',
  'external-director',
  'external-superadmin',
];
// Of those, only these may toggle the preference; internal-user is view-only.
const EDIT_ROLES: Role[] = [
  'director',
  'external-director',
  'external-superadmin',
];

export const NotificationPreferencesView = () => {
  const navigate = useNavigate();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const handler = new PushNotificationPreferenceHandler();

  const userId =
    useAppSelector((state) => state.authReducer).userInfo?.userId ?? '';
  const canEdit = useRoleBasedAccess(EDIT_ROLES);
  const { preferences } = useAppSelector(
    (state) => state.pushNotificationPreferenceReducer
  );

  // Toggling only stages a change locally; nothing is sent to the server
  // until Save is clicked.
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>(
    {}
  );
  const [saving, setSaving] = useState(false);

  // Sub-clients' users — a fully separate section/state from the self-service
  // list above, keyed by "userId:customerId" rather than just customerId, so
  // it can never collide with the caller's own preference for the same site.
  interface ManagedUser {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    tpmsAlertsEnabled: boolean;
  }
  interface ManagedCustomer {
    customerId: string;
    customerCode: string;
    customerName: string;
    users: ManagedUser[];
  }
  const [managedCustomers, setManagedCustomers] = useState<ManagedCustomer[]>(
    []
  );
  const [managedPendingChanges, setManagedPendingChanges] = useState<
    Record<string, boolean>
  >({});
  const [managedSaving, setManagedSaving] = useState(false);

  const managedKey = (userId2: string, customerId: string) =>
    `${userId2}:${customerId}`;

  useEffect(() => {
    if (userId) void handler.getAll(userId);
    if (userId && canEdit) {
      void handler.getManagedUsers(userId).then(setManagedCustomers);
    }
  }, [userId, canEdit]);

  const handleToggle = (customerId: string, tpmsAlertsEnabled: boolean) => {
    if (!canEdit) return;
    setPendingChanges((prev) => {
      const original = preferences.find(
        (p) => p.customerId === customerId
      )?.tpmsAlertsEnabled;
      if (original === tpmsAlertsEnabled) {
        const { [customerId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [customerId]: tpmsAlertsEnabled };
    });
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const handleSave = async () => {
    const changes = Object.entries(pendingChanges).map(
      ([customerId, tpmsAlertsEnabled]) => ({ customerId, tpmsAlertsEnabled })
    );
    if (!changes.length) return;

    setSaving(true);
    try {
      const { failedCustomerIds } = await handler.savePreferences(
        userId,
        changes
      );
      // clear only the changes that actually saved; leave failed ones pending
      setPendingChanges((prev) => {
        const next: Record<string, boolean> = {};
        failedCustomerIds.forEach((id) => {
          if (id in prev) next[id] = prev[id];
        });
        return next;
      });
    } finally {
      setSaving(false);
    }
  };

  const handleManagedToggle = (
    managedUserId: string,
    customerId: string,
    tpmsAlertsEnabled: boolean
  ) => {
    const key = managedKey(managedUserId, customerId);
    setManagedPendingChanges((prev) => {
      const customer = managedCustomers.find(
        (c) => c.customerId === customerId
      );
      const original = customer?.users.find(
        (u) => u.userId === managedUserId
      )?.tpmsAlertsEnabled;
      if (original === tpmsAlertsEnabled) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: tpmsAlertsEnabled };
    });
  };

  const hasManagedPendingChanges =
    Object.keys(managedPendingChanges).length > 0;

  const handleManagedSave = async () => {
    const changes = Object.keys(managedPendingChanges).map((key) => {
      const [managedUserId, customerId] = key.split(':');
      return {
        userId: managedUserId,
        customerId,
        tpmsAlertsEnabled: managedPendingChanges[key],
      };
    });
    if (!changes.length) return;

    setManagedSaving(true);
    try {
      const { failedKeys } = await handler.saveManagedPreferences(changes);

      setManagedCustomers((prev) =>
        prev.map((customer) => ({
          ...customer,
          users: customer.users.map((managedUser) => {
            const key = managedKey(managedUser.userId, customer.customerId);
            if (key in managedPendingChanges && !failedKeys.includes(key)) {
              return {
                ...managedUser,
                tpmsAlertsEnabled: managedPendingChanges[key],
              };
            }
            return managedUser;
          }),
        }))
      );

      setManagedPendingChanges((prev) => {
        const next: Record<string, boolean> = {};
        failedKeys.forEach((key) => {
          if (key in prev) next[key] = prev[key];
        });
        return next;
      });
    } finally {
      setManagedSaving(false);
    }
  };

  return (
    <Permission roles={VIEW_ROLES} fallbackComponent={<NoAccess />}>
      <Box display={'flex'} flexDirection={'column'} p={2}>
        <Paper
          sx={{
            display: 'flex',
            gap: 5,
            p: 2,
            alignItems: 'center',
            backgroundColor: palette.toolBarBackground,
          }}
        >
          <CustomLoadingButton
            startIcon={<ArrowBack />}
            onClick={() => {
              navigate(-1);
            }}
          >
            Back
          </CustomLoadingButton>
          <Typography sx={{ color: palette.textPrimary, fontSize: '1.4rem' }}>
            TPMS Notification Preferences
          </Typography>
          {canEdit && (
            <CustomLoadingButton
              variant="contained"
              sx={{ ml: 'auto' }}
              disabled={!hasPendingChanges}
              loading={saving}
              onClick={() => {
                void handleSave();
              }}
            >
              Save
            </CustomLoadingButton>
          )}
        </Paper>

        <Paper sx={{ mt: 2, p: 2, backgroundColor: palette.toolBarBackground }}>
          <Box
            component="ul"
            sx={{ m: 0, mb: 2, pl: 2.5, color: palette.textSecondary }}
          >
            <Typography component="li" variant="body2">
              Enable/Disable TPMS notifications at site level for your account.
            </Typography>
            <Typography component="li" variant="body2">
              Any other notifications other than TPMS will remain unaffected.
            </Typography>
            <Typography component="li" variant="body2">
              This would impact push notification for both iOS &amp; Android.
            </Typography>
            {!canEdit && (
              <Typography component="li" variant="body2">
                You have view-only access to these preferences.
              </Typography>
            )}
            {canEdit && (
              <Typography component="li" variant="body2">
                Toggles marked with * are unsaved — click Save to apply them.
              </Typography>
            )}
          </Box>

          {preferences.length === 0 ? (
            <Typography variant="body2" sx={{ color: palette.textSecondary }}>
              You don&apos;t belong to any clients yet.
            </Typography>
          ) : (
            <Table>
              <TableBody>
                {preferences.map((preference) => {
                  const checked =
                    pendingChanges[preference.customerId] ??
                    preference.tpmsAlertsEnabled;
                  const isDirty = preference.customerId in pendingChanges;
                  return (
                    <TableRow key={preference.customerId}>
                      <TableCell
                        sx={{ borderColor: palette.toolBarBackground }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ color: palette.textPrimary }}
                        >
                          {preference.customerName}
                          {isDirty && ' *'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: palette.textSecondary }}
                        >
                          {preference.customerCode}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ borderColor: palette.toolBarBackground }}
                      >
                        <Switch
                          checked={checked}
                          disabled={!canEdit || saving}
                          onChange={(_, next) => {
                            handleToggle(preference.customerId, next);
                          }}
                          color="primary"
                          inputProps={{
                            'aria-label': `TPMS notifications for ${preference.customerName}`,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>

        {canEdit && (
          <Paper
            sx={{ mt: 2, p: 2, backgroundColor: palette.toolBarBackground }}
          >
            <Box display={'flex'} alignItems={'center'} gap={2} sx={{ mb: 2 }}>
              <Typography
                sx={{ color: palette.textPrimary, fontSize: '1.2rem' }}
              >
                Notifications for Sub-Client Users
              </Typography>
              <CustomLoadingButton
                variant="contained"
                sx={{ ml: 'auto' }}
                disabled={!hasManagedPendingChanges}
                loading={managedSaving}
                onClick={() => {
                  void handleManagedSave();
                }}
              >
                Save
              </CustomLoadingButton>
            </Box>

            {managedCustomers.length === 0 ? (
              <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                None of your sub-clients have any users yet.
              </Typography>
            ) : (
              managedCustomers.map((customer) => (
                <Box key={customer.customerId} sx={{ mb: 3 }}>
                  <Typography
                    variant="body1"
                    sx={{ color: palette.textPrimary, fontWeight: 'bold' }}
                  >
                    {customer.customerName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: palette.textSecondary }}
                  >
                    {customer.customerCode}
                  </Typography>
                  <Table>
                    <TableBody>
                      {customer.users.map((managedUser) => {
                        const key = managedKey(
                          managedUser.userId,
                          customer.customerId
                        );
                        const checked =
                          managedPendingChanges[key] ??
                          managedUser.tpmsAlertsEnabled;
                        const isDirty = key in managedPendingChanges;
                        return (
                          <TableRow key={key}>
                            <TableCell
                              sx={{ borderColor: palette.toolBarBackground }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: palette.textPrimary }}
                              >
                                {managedUser.firstName} {managedUser.lastName}
                                {isDirty && ' *'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: palette.textSecondary }}
                              >
                                {managedUser.email}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ borderColor: palette.toolBarBackground }}
                            >
                              <Switch
                                checked={checked}
                                disabled={managedSaving}
                                onChange={(_, next) => {
                                  handleManagedToggle(
                                    managedUser.userId,
                                    customer.customerId,
                                    next
                                  );
                                }}
                                color="primary"
                                inputProps={{
                                  'aria-label': `TPMS notifications for ${managedUser.firstName} ${managedUser.lastName} at ${customer.customerName}`,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              ))
            )}
          </Paper>
        )}
      </Box>
    </Permission>
  );
};
